import React, { useState } from 'react';
import useGet from '../Hooks/useGet';
import API_ENDPOINTS from '../Config/api';
import { useTranslation } from 'react-i18next';
import { Package, Calendar, CreditCard, Monitor, Shield, Radio, ChevronDown, ChevronUp, Hash } from 'lucide-react';

const STATO_CONFIG = {
  'DA FATTURARE': { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', dot: 'bg-amber-400' },
  'APERTO': { bg: 'bg-sky-50', border: 'border-sky-200', text: 'text-sky-700', dot: 'bg-sky-400' },
  'CHIUSO': { bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-500', dot: 'bg-slate-400' },
  'AUTORINNOVO': { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', dot: 'bg-emerald-400' },
  'CANCELLATO': { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-600', dot: 'bg-red-400' },
};

function formatCurrency(val) {
  if (val == null) return '—';
  return parseFloat(val).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
}

function formatDate(val) {
  if (!val) return '—';
  return new Date(val).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
}

function StatoBadge({ stato }) {
  const cfg = STATO_CONFIG[stato] || STATO_CONFIG['CHIUSO'];
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full ${cfg.bg} ${cfg.border} ${cfg.text} border`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {stato}
    </span>
  );
}

function OrdineCard({ o }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="group bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden">
      {/* Top accent bar */}
      <div className="h-1 bg-gradient-to-r from-yellow-400 via-amber-400 to-orange-400" />

      <div className="p-5">
        {/* Header row */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-1.5">
              <div className="flex items-center gap-1.5 text-gray-400">
                <Hash size={14} />
                <span className="text-base font-bold text-gray-800">{o.numero_ordine || '—'}</span>
              </div>
              <StatoBadge stato={o.stato_ordine} />
            </div>
            {o.prodotto_base_info && (
              <p className="text-sm text-gray-500">
                <span className="font-medium text-gray-700">{o.prodotto_base_info.nome_prodotto}</span>
                <span className="mx-1.5 text-gray-300">•</span>
                <span className="text-xs font-mono text-gray-400">{o.prodotto_base_info.codice_prodotto}</span>
              </p>
            )}
          </div>
          <div className="text-right shrink-0">
            <div className="text-xl font-bold text-gray-900">{formatCurrency(o.totale_importo)}</div>
            <div className="text-[11px] text-gray-400 mt-0.5">IVA {parseFloat(o.iva).toFixed(0)}% inclusa</div>
          </div>
        </div>

        {/* Services pills */}
        {(o.awareness_enabled || o.offerta_sorveglianza_rete || o.servizio_mssp_monitoring) && (
          <div className="flex flex-wrap gap-2 mt-4">
            {o.awareness_enabled && (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-violet-50 text-violet-600 pl-2 pr-3 py-1.5 rounded-lg border border-violet-100">
                <Shield size={13} />
                Awareness · {o.awareness_seats} utenze
              </span>
            )}
            {o.offerta_sorveglianza_rete && (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-blue-50 text-blue-600 pl-2 pr-3 py-1.5 rounded-lg border border-blue-100">
                <Radio size={13} />
                Network Surveillance
              </span>
            )}
            {o.servizio_mssp_monitoring && (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-teal-50 text-teal-600 pl-2 pr-3 py-1.5 rounded-lg border border-teal-100">
                <Monitor size={13} />
                MSSP Monitoring · {o.gettoni_totali} gettoni
              </span>
            )}
          </div>
        )}

        {/* Expandable details */}
        <button
          onClick={() => setExpanded(v => !v)}
          className="mt-4 flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors"
        >
          {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          {expanded ? 'Nascondi dettagli' : 'Mostra dettagli'}
        </button>

        {expanded && (
          <div className="mt-3 pt-3 border-t border-gray-50 grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm animate-in fade-in duration-200">
            <div className="flex items-start gap-2">
              <Package size={15} className="text-gray-300 mt-0.5 shrink-0" />
              <div>
                <span className="text-[11px] text-gray-400 uppercase tracking-wide block">Endpoint</span>
                <span className="font-semibold text-gray-700">{o.numero_endpoint ?? '—'}</span>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <CreditCard size={15} className="text-gray-300 mt-0.5 shrink-0" />
              <div>
                <span className="text-[11px] text-gray-400 uppercase tracking-wide block">Netto</span>
                <span className="font-semibold text-gray-700">{formatCurrency(o.importo_euro)}</span>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Calendar size={15} className="text-gray-300 mt-0.5 shrink-0" />
              <div>
                <span className="text-[11px] text-gray-400 uppercase tracking-wide block">Rinnovo</span>
                <span className="font-semibold text-gray-700">{formatDate(o.data_rinnovo)}</span>
              </div>
            </div>
            <div>
              <span className="text-[11px] text-gray-400 uppercase tracking-wide block">Pagamento</span>
              {o.flag_pagamento_eseguito
                ? <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />Eseguito</span>
                : <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-600"><span className="w-1.5 h-1.5 rounded-full bg-amber-400" />In attesa</span>
              }
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function MieiOrdini() {
  const { fetchedData: ordini, loading, error } = useGet(API_ENDPOINTS.myOrders);
  const { t } = useTranslation();

  return (
    <div className="p-6 lg:p-8 max-w-5xl mx-auto">
      {/* Page header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center shadow-sm">
            <Package size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">I miei Ordini</h1>
            <p className="text-sm text-gray-400">Storico e dettagli degli ordini effettuati</p>
          </div>
        </div>
      </div>

      {loading && (
        <div className="flex flex-col items-center justify-center py-24 gap-3">
          <div className="animate-spin rounded-full h-10 w-10 border-[3px] border-gray-200 border-t-yellow-500" />
          <span className="text-sm text-gray-400">Caricamento ordini...</span>
        </div>
      )}

      {error && !loading && (
        <div className="bg-red-50 border border-red-100 text-red-600 px-5 py-4 rounded-xl text-sm">
          Errore nel caricamento degli ordini. Riprova più tardi.
        </div>
      )}

      {!loading && !error && ordini && ordini.length === 0 && (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <div className="w-16 h-16 rounded-full bg-gray-50 flex items-center justify-center">
            <Package size={28} className="text-gray-300" />
          </div>
          <p className="text-gray-400 text-sm">Nessun ordine presente</p>
        </div>
      )}

      {!loading && ordini && ordini.length > 0 && (
        <div className="space-y-4">
          {ordini.map((o) => (
            <OrdineCard key={o.id_ordine} o={o} />
          ))}
        </div>
      )}
    </div>
  );
}
